import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Container, InputGroup, Row } from 'react-bootstrap';

import { AuthenticatedContextConsumer } from '../../context/AuthenticatedContext';
import Customer, { CustomerPropType } from '../../models/data/Customer';
import CustomerEquipment, {
  CustomerEquipmentPropType,
} from '../../models/data/CustomerEquipment';
import MeasurementDeviceItem, {
  MeasurementDeviceItemArrayPropType,
} from '../../models/data/MeasurementDeviceItem';
import SelectCustomers from '../select-customers';
import SelectEquipments from '../select-equipments';
import SelectItems from '../select-items';

export interface SelectPanelProps {
  className?: string;
  selectedCustomer?: Customer | null;
  selectedCustomerEquipment?: CustomerEquipment | null;
  selectedMeasurementDeviceItems?: MeasurementDeviceItem[] | null;
  onChange?: (
    measurementDeviceItems: MeasurementDeviceItem[],
    customerEquipment?: CustomerEquipment | null,
    customer?: Customer | null,
  ) => void;
}

const SelectPanel: React.FunctionComponent<SelectPanelProps> = ({
  className,
  selectedCustomer,
  selectedCustomerEquipment,
  selectedMeasurementDeviceItems,
  onChange,
}) => {
  const [selected, setSelected] = useState({
    customer: selectedCustomer,
    customerEquipment: selectedCustomerEquipment,
    measurementDeviceItems: selectedMeasurementDeviceItems ?? [],
  });

  useEffect(() => {
    setSelected({
      ...selected,
      customer: selectedCustomer,
    });
  }, [selectedCustomer]);
  useEffect(() => {
    setSelected({
      ...selected,
      customer: selectedCustomerEquipment,
      customerEquipment: selectedCustomerEquipment,
    });
  }, [selectedCustomerEquipment]);
  useEffect(() => {
    const items = selectedMeasurementDeviceItems ?? [];
    setSelected({
      ...selected,
      customer: items[0] ?? selected.customer,
      customerEquipment: items[0] ?? selected.customerEquipment,
      measurementDeviceItems: items,
    });
  }, [selectedMeasurementDeviceItems]);

  const onChangeSelectCustomer = (newCustomer?: Customer | null) => {
    const { customerEquipment, measurementDeviceItems } = selected;
    const newCustomerEquipment =
      newCustomer != null &&
      customerEquipment != null &&
      newCustomer.customerId === customerEquipment.customerId
        ? customerEquipment
        : null;
    const newMeasurementDeviceItems =
      newCustomerEquipment != null
        ? measurementDeviceItems.filter(
            (i) =>
              i.customerEquipmentId ===
              newCustomerEquipment.customerEquipmentId,
          )
        : [];
    setSelected({
      customer: newCustomer,
      customerEquipment: newCustomerEquipment,
      measurementDeviceItems: newMeasurementDeviceItems,
    });
    if (onChange != null) {
      onChange(newMeasurementDeviceItems, newCustomerEquipment, newCustomer);
    }
  };
  const onChangeSelectCustomerEquipment = (
    newCustomerEquipment?: CustomerEquipment | null,
  ) => {
    const { customer, measurementDeviceItems } = selected;
    const newCustomer = newCustomerEquipment ?? customer;
    const newMeasurementDeviceItems =
      newCustomerEquipment != null
        ? measurementDeviceItems.filter(
            (i) =>
              i.customerEquipmentId ===
              newCustomerEquipment.customerEquipmentId,
          )
        : [];
    setSelected({
      customer: newCustomer,
      customerEquipment: newCustomerEquipment,
      measurementDeviceItems: newMeasurementDeviceItems,
    });
    if (onChange != null) {
      onChange(newMeasurementDeviceItems, newCustomerEquipment, newCustomer);
    }
  };
  const onChangeSelectMeasurementDeviceItems = (
    newMeasurementDeviceItems: MeasurementDeviceItem[],
  ) => {
    const { customer, customerEquipment } = selected;
    const newCustomer = newMeasurementDeviceItems[0] ?? customer;
    const newCustomerEquipment =
      newMeasurementDeviceItems[0] ?? customerEquipment;
    setSelected({
      customer: newCustomer,
      customerEquipment: newCustomerEquipment,
      measurementDeviceItems: newMeasurementDeviceItems,
    });
    if (onChange != null) {
      onChange(newMeasurementDeviceItems, newCustomerEquipment, newCustomer);
    }
  };

  return (
    <AuthenticatedContextConsumer>
      {({ loginCustomer }) => {
        const accessType = loginCustomer?.accessType;
        const canSelectCustomer =
          accessType === 'Admin' || accessType === 'Mec';
        return (
          <Container fluid className={className}>
            {canSelectCustomer ? (
              <Row>
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>最終需要家</InputGroup.Text>
                    </InputGroup.Prepend>
                    <SelectCustomers
                      className="flex-fill"
                      isDisabled={!canSelectCustomer}
                      selectedCustomer={selected.customer}
                      onChange={onChangeSelectCustomer}
                    />
                  </InputGroup>
                </Col>
              </Row>
            ) : undefined}
            <Row>
              <Col>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>顧客設備名</InputGroup.Text>
                  </InputGroup.Prepend>
                  <SelectEquipments
                    className="flex-fill"
                    isDisabled={canSelectCustomer && selected.customer == null}
                    customerId={selected.customer?.customerId}
                    selectedCustomerEquipment={selected.customerEquipment}
                    onChange={onChangeSelectCustomerEquipment}
                  />
                </InputGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <SelectItems
                  className="flex-fill"
                  isDisabled={selected.customerEquipment == null}
                  customerEquipmentId={
                    selected.customerEquipment?.customerEquipmentId
                  }
                  selectedMeasurementDeviceItems={
                    selected.measurementDeviceItems
                  }
                  onChange={onChangeSelectMeasurementDeviceItems}
                />
              </Col>
            </Row>
          </Container>
        );
      }}
    </AuthenticatedContextConsumer>
  );
};

SelectPanel.propTypes = {
  className: PropTypes.string,
  selectedCustomer: CustomerPropType,
  selectedCustomerEquipment: CustomerEquipmentPropType,
  selectedMeasurementDeviceItems: MeasurementDeviceItemArrayPropType,
  onChange: PropTypes.func,
};

SelectPanel.defaultProps = {
  className: undefined,
  selectedCustomer: undefined,
  selectedCustomerEquipment: undefined,
  selectedMeasurementDeviceItems: undefined,
  onChange: undefined,
};

export default SelectPanel;
